import {

  ChangeDetectionStrategy,

  ChangeDetectorRef,

  Component,

  OnDestroy,

  OnInit,

} from '@angular/core';

import { NavigationEnd, Router } from '@angular/router';

import { filter, Subscription } from 'rxjs';

import { PopupAdDto } from '../../../core/models/popup-ad.model';

import { PopupAdService } from '../../../core/services/popup-ad.service';

import {

  getPopupMediaOrientation,

  isVideoMediaUrl,

  PopupMediaOrientation,

} from '../../../core/utils/popup-media.util';

import { AppPlatformService } from '../../../core/services/app-platform.service';

import { popupSpringAnimation } from '../../animations';



const SESSION_KEY_PREFIX = 'zt_popup_ad_session_';

const USER_KEY_PREFIX = 'zt_popup_ad_user_';



@Component({

  selector: 'app-storefront-popup-ad',

  templateUrl: './storefront-popup-ad.component.html',

  styleUrls: ['./storefront-popup-ad.component.scss'],

  animations: [popupSpringAnimation],

  changeDetection: ChangeDetectionStrategy.OnPush,

})

export class StorefrontPopupAdComponent implements OnInit, OnDestroy {

  popup: PopupAdDto | null = null;

  visible = false;

  closing = false;

  /** Reserved box — do not switch orientation classes after load (CLS). */

  mediaOrientation: PopupMediaOrientation | null = null;



  private cancelDefer: (() => void) | null = null;

  private pendingPopup: PopupAdDto | null = null;

  private routerSub: Subscription | null = null;



  constructor(

    private popupAdService: PopupAdService,

    private cdr: ChangeDetectorRef,

    private platform: AppPlatformService,

    private router: Router

  ) {}



  ngOnInit(): void {

    if (!this.platform.isBrowser) {

      return;

    }

    this.popupAdService.getActivePopup().subscribe((popup) => {

      if (!popup || !this.shouldShow(popup)) {

        return;

      }

      this.pendingPopup = popup;

      this.revealPopup();

    });



    this.routerSub = this.router.events

      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))

      .subscribe(() => {

        if (this.isCheckoutBlockingRoute() && this.visible) {

          this.forceHide();

        }

      });

  }



  ngOnDestroy(): void {

    this.cancelDefer?.();

    this.cancelDefer = null;

    this.routerSub?.unsubscribe();

    this.routerSub = null;

  }



  get hasTextContent(): boolean {

    const popup = this.popup;

    if (!popup) {

      return false;

    }

    return !!(popup.title?.trim() || popup.contentHtml?.trim() || popup.buttonText?.trim());

  }



  get isMediaOnly(): boolean {

    return !!this.popup?.imageUrl && !this.hasTextContent;

  }



  close(event?: Event): void {

    event?.stopPropagation();

    if (!this.popup || this.closing) {

      return;

    }



    this.persistDismissal(this.popup);

    this.forceHide();

  }



  onBackdropClick(event: MouseEvent): void {

    if ((event.target as HTMLElement).classList.contains('popup-ad-overlay')) {

      this.close(event);

    }

  }



  onActionClick(): void {

    const url = this.popup?.buttonUrl?.trim();

    if (url) {

      window.open(url, '_blank', 'noopener,noreferrer');

    }

    this.close();

  }



  onMediaLoaded(event: Event): void {

    const target = event.target;

    if (!(target instanceof HTMLImageElement || target instanceof HTMLVideoElement)) {

      return;

    }



    const width = target instanceof HTMLVideoElement ? target.videoWidth : target.naturalWidth;

    const height = target instanceof HTMLVideoElement ? target.videoHeight : target.naturalHeight;

    this.mediaOrientation = getPopupMediaOrientation(width, height);

    this.cdr.detectChanges();

  }



  isVideoUrl(url?: string | null): boolean {

    return isVideoMediaUrl(url);

  }



  private revealPopup(): void {

    if (!this.pendingPopup || this.isCheckoutBlockingRoute()) {

      return;

    }

    if (!this.shouldShow(this.pendingPopup)) {

      this.pendingPopup = null;

      return;

    }

    this.popup = this.pendingPopup;

    this.visible = true;

    this.closing = false;

    this.cdr.detectChanges();

  }



  private forceHide(): void {

    this.cancelDefer?.();

    this.cancelDefer = null;

    this.pendingPopup = null;

    this.closing = false;

    this.visible = false;

    this.popup = null;

    this.mediaOrientation = null;

    this.cdr.detectChanges();

  }



  private isCheckoutBlockingRoute(): boolean {

    const path = (this.router.url || '').split('?')[0].toLowerCase();

    return path === '/cart' || path === '/checkout' || path.startsWith('/cart/') || path.startsWith('/checkout/');

  }



  private shouldShow(popup: PopupAdDto): boolean {

    if (!popup.isActive) {

      return false;

    }



    if (this.isCheckoutBlockingRoute()) {

      return false;

    }



    if (popup.showFrequency === 2) {

      return typeof localStorage !== 'undefined' && !localStorage.getItem(`${USER_KEY_PREFIX}${popup.id}`);

    }



    // Frequency 0 (every visit) and 1 (once per session) both use session dismiss so a closed

    // popup does not immediately reappear on the same tab refresh.

    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(`${SESSION_KEY_PREFIX}${popup.id}`)) {

      return false;

    }



    return true;

  }



  private persistDismissal(popup: PopupAdDto): void {

    if (popup.showFrequency === 2) {

      if (typeof localStorage !== 'undefined') {

        localStorage.setItem(`${USER_KEY_PREFIX}${popup.id}`, '1');

      }

      return;

    }



    if (typeof sessionStorage !== 'undefined') {

      sessionStorage.setItem(`${SESSION_KEY_PREFIX}${popup.id}`, '1');

    }

  }

}


